if ( typeof CSSSlider !== 'function' ) {
  class CSSSlider extends HTMLElement {

    constructor(){

      super();

      this._prefix = window.KT_PREFIX || '';

      this._touchScreen = document.body.classList.contains('touchevents');

      this._rtl = document.documentElement.getAttribute('dir') == 'rtl';

      // create option object, from defaults
      this.o = {
        ...{
          domReady: false,
          selector: `.${this._prefix}css-slide`, 
          snapping: true, 
          groupCells: false,
          forceOneSlidePerPage: false,
          autoHeight: false, 
          navigation: true,
          navigationDOM: `<span class="${this._prefix}css-slider-button ${this._prefix}css-slider-prev" style="display:none">←</span>
            <span class="${this._prefix}css-slider-button ${this._prefix}css-slider-next" style="display:none">→</span>`,
          thumbnails: true,
          thumbnailsDOM: `<div class="${this._prefix}css-slider-dot-navigation" style="display:none"></div>`,
          thumbnailsNoInteraction: false,
          indexNav: false,
          indexNavDOM: `<div class="${this._prefix}css-slider-index-navigation"><span class="${this._prefix}css-slider-current">1</span> / <span class="${this._prefix}css-slider-total">1</span></div>`,
          watchCSS: false,
          undisplay: false,
          disableSwipe: false,
          listenScroll: false,
          observer: true,
          disableMouseDownEvent: true,
          disableTabNavigation: false,
          autoplay: 0,
          loop: false,
          forceFirstSlide: false
        }, ...JSON.parse(this.dataset.options)
      };

      // init slider or watch for css
      if ( ! this.o.watchCSS ) {
        this.initSlider();
      } else {
        this.RESIZE_WATCHER = debounce(()=>{
          const afterContent = window.getComputedStyle(this,':after').content;
          if ( afterContent.includes('css-slide') && !this.sliderEnabled ) {
            this.initSlider();
          } else if ( !afterContent.includes('css-slide') && this.sliderEnabled )  { 
            this.destroySlider();
          }
        }, 100);
        window.addEventListener('resize', this.RESIZE_WATCHER);
        this.RESIZE_WATCHER();
      }

    }

    destroySlider(){
      this._destroyLoop();
      this.innerHTML = `${this.originalHTML}`;
      this.classList.remove(`${this._prefix}enabled`);
      this.sliderEnabled = false;
      window.removeEventListener('resize', this.RESIZE_EVENT);
      window.removeEventListener('scroll', this.SCROLL_EVENT);
      this.element.removeEventListener('scroll', this.SCROLL_EVENT_ANIMATIONS);
      this.dispatchEvent(new CustomEvent('destroy'));
    }

    disconnectedCallback(){
      if ( this.RESIZE_WATCHER ) {
        window.removeEventListener('resize', this.RESIZE_WATCHER);
      }
      if ( this.RESIZE_EVENT ) {
        window.removeEventListener('resize', this.RESIZE_EVENT);
      }
      if ( this.SCROLL_EVENT ) {
        window.removeEventListener('scroll', this.SCROLL_EVENT);
      }
      if ( this.element && this.SCROLL_EVENT_ANIMATIONS ) {
        this.element.removeEventListener('scroll', this.SCROLL_EVENT_ANIMATIONS);
      }
      this._destroyLoop();
      if ( this._autoplayInterval ) {
        clearInterval(this._autoplayInterval);
      }
    }

    initSlider(){

      // create custom events

      this._readyEvent = new CustomEvent('ready');
      this._changeEvent = new CustomEvent('change');
      this._scrollEvent = new CustomEvent('scroll');
      this._navEvent = new CustomEvent('navigation');
      this._resetEvent = new CustomEvent('reset');
      this._pointerDownEvent = new CustomEvent('pointerDown');
      this._pointerUpEvent = new CustomEvent('pointerUp');

      // create slider structure

      this.classList.add(`${this._prefix}css-slider`);
      if ( ! this.o.domReady ) {
        this.originalHTML = this.innerHTML;
        this.innerHTML = `<div class="${this._prefix}css-slider-viewport">
          <div class="${this._prefix}css-slider-holder" ${this.o.disableTabNavigation ? 'tabindex="-1"' : ''}>
            <div class="${this._prefix}css-slider-container">
              ${this.originalHTML}
            </div>
          </div>
        </div>`;
      }

      // add css-slide to children, if it's not set
      if ( this.o.undisplay ) {
        this.querySelectorAll(`${this.o.selector}`).forEach((elm)=>{
          elm.style.display = 'block';
        })
      }

      if ( this.o.selector != `.${this._prefix}css-slide` ) {
        this.querySelectorAll(`${this.o.selector}`).forEach((elm)=>{
          elm.classList.add(`${this._prefix}css-slide`);
        })
      }

      // setup variables

      this.element = this.querySelector(`.${this._prefix}css-slider-holder`);
      if ( this.o.groupCells ) {
        this.element.scrollLeft = 0;
      }

      this.items = this.o.loop
        ? Array.from(this.querySelectorAll(`${this.o.selector}`)).filter(elm => !elm.hasAttribute('data-css-slider-loop-clone'))
        : this.querySelectorAll(`${this.o.selector}`);
      this.indexedItems = [];
      this.index = 0;
      this.length = this.items.length;
      this.windowWidth = window.innerWidth;
      this._forcedSlideChange = false;

      if ( this.o.loop && this.items.length > 1 ) {
        this._createLoopClones();
      }

      if ( this.o.disableMouseDownEvent ) {
        this.querySelector(`.${this._prefix}css-slider-container`).addEventListener('mousedown', e=>{
          if ( e.target.tagName !== 'SELECT' ) {
            e.preventDefault();
          }
        })
      }
      
      this.viewport = this.querySelector(`.${this._prefix}css-slider-viewport`);
      if ( this.o.autoHeight ) {
        this.viewport.classList.add('auto-height');
      }

      // append navigation

      if ( this.o.navigation || this.o.thumbnails || this.indexNav ) {

        const navigationElements = new DOMParser().parseFromString(this.o.navigationDOM, 'text/html');
        let container = document.createElement('div');
        container.classList.add(`${this._prefix}css-slider-navigation-container`);
        container.innerHTML = `
          ${(this.o.navigation ? navigationElements.querySelector(`.${this._prefix}css-slider-prev`).outerHTML : '')}
          ${(this.o.thumbnails ? this.o.thumbnailsDOM : '')}
          ${(this.o.navigation ? navigationElements.querySelector(`.${this._prefix}css-slider-next`).outerHTML : '')}
          ${(this.o.indexNav ? this.o.indexNavDOM : '')}`;

        if ( this.o.navigation ) {

          this.prevEl = container.querySelector(`.${this._prefix}css-slider-prev`);
          this.prevEl.setAttribute('tabindex', 0);
          this.prevEl.setAttribute('role', 'button');
          this.prevEl.addEventListener('click', e=>{
            e.preventDefault();
            this.changeSlide('prev');
            this.dispatchEvent(this._navEvent);
          });
          this.prevEl.addEventListener('keydown', e=>{
				    if ( e.keyCode == window.KEYCODES.RETURN ) {
              this.prevEl.click();
            }
          });
          this.prevEl.querySelector('svg').setAttribute('aria-hidden', 'true');
          const prevElA11y = document.createElement('span');
          prevElA11y.textContent = window.KROWN.settings.locales.slider_prev_button_label;
          prevElA11y.classList.add(`${this._prefix}visually-hidden`);
          this.prevEl.appendChild(prevElA11y);

          this.nextEl = container.querySelector(`.${this._prefix}css-slider-next`);
          this.nextEl.setAttribute('tabindex', 0);
          this.nextEl.setAttribute('role', 'button');
          this.nextEl.addEventListener('click', e=>{
            e.preventDefault();
            this.changeSlide('next');
            this.dispatchEvent(this._navEvent);
          });
          this.nextEl.addEventListener('keydown', e=>{
				    if ( e.keyCode == window.KEYCODES.RETURN ) {
              this.nextEl.click();
            }
          });
          this.nextEl.querySelector('svg').setAttribute('aria-hidden', 'true');
          const nextElA11y = document.createElement('span');
          nextElA11y.textContent = window.KROWN.settings.locales.slider_next_button_label;
          nextElA11y.classList.add(`${this._prefix}visually-hidden`);
          this.nextEl.appendChild(nextElA11y);

        }

        if ( this.o.thumbnails ) {
          this.thumbnailsEl = container.querySelector(`.${this._prefix}css-slider-dot-navigation`); 
        }

        if ( this.o.indexNav ) {
          this.indexEl = container.querySelector(`.${this._prefix}css-slider-current`);
          this.lengthEl = container.querySelector(`.${this._prefix}css-slider-total`); 
        }

        this.append(container);

      }

      if ( this.length > 1 ) {

        // add observer
        
        if ( this.o.observer ) {

          this.OBSERVER = new IntersectionObserver(entries=>{
            if ( ! this._sliderBlockScroll ) {
              entries.forEach(entry=>{
                if ( entry.intersectionRatio >= .5 ) {
                  this.index = parseInt(entry.target.getAttribute('data-index'));
                  this.checkSlide();
                  this.dispatchEvent(this._changeEvent);
                }
              })
            }
          }, {
            threshold: [0, .5]
          }); 

        } else {

          this.SCROLL_EVENT = debounce(()=>{
            if ( ! this._sliderBlockScroll ) {
              const scrollItems = this.indexedItems.entries();
              const scrollArray = Array.from(scrollItems, elm => Math.abs(elm[1].offsetLeft-this.element.scrollLeft));
              const scrollDistance = Math.min(...scrollArray);
              const scrollIndex = scrollArray.indexOf(scrollDistance);
              if ( scrollIndex != this.index ) {
                this.index = scrollIndex;
                this.checkSlide();
                this.dispatchEvent(this._changeEvent);
              }
            }
          }, 10);

          this.element.addEventListener('scroll', this.SCROLL_EVENT, {passive:true});

        }

        // reset on resize

        this.RESIZE_EVENT = debounce(()=>{
          if ( this.windowWidth != window.innerWidth && this.o.groupCells) {
            this.resetSlider();
          }
          if ( ! this.o.groupCells ) {
            this.checkSlide();
          }
          this.windowWidth = window.innerWidth;
        }, 100);
        window.addEventListener('resize', this.RESIZE_EVENT);
        this.resetSlider(true);

        if ( this.o.loop ) {
          this._positionLoopAtCurrent();
          this._initLoopScrollWatcher();
        }

        // dispatching scroll event, mostly for extra animations
        if ( this.o.listenScroll ) {
          this.SCROLL_EVENT_ANIMATIONS = (()=>{
            let slidesWidth = -this.querySelector(`.${this._prefix}css-slider-container`).offsetWidth;
            this.items.forEach(elm=>{slidesWidth += elm.offsetWidth});
            this.progress = this.element.scrollLeft / slidesWidth;
            this.dispatchEvent(this._scrollEvent);
          });
          this.element.addEventListener('scroll', this.SCROLL_EVENT_ANIMATIONS, {passive:true});
        }

        if ( ! this.o.disableSwipe && ! this._touchScreen && ! this.element.classList.contains(`${this._prefix}css-slider--singular`) ) {
          this.element.addEventListener('mousedown', e=>{
            if ( ! this.element.classList.contains(`${this._prefix}css-slider--disable-dragging`) ) {
              this.mouseX = e.screenX;
              this.element.classList.add(`${this._prefix}can-drag`);
              this.element.classList.add(`${this._prefix}mouse-down`);
            }
          });
          this.element.addEventListener('mouseup', e=>{
            this.element.classList.remove(`${this._prefix}mouse-down`);
            this.element.classList.remove(`${this._prefix}can-drag`);
            this.element.classList.remove(`${this._prefix}pointer-events-off`);
            if ( this._pot ) clearTimeout(this._pot);
          });

          this.element.addEventListener('mouseleave', e=>{
            this.element.classList.remove(`${this._prefix}mouse-down`);
            this.element.classList.remove(`${this._prefix}can-drag`);
            this.element.classList.remove(`${this._prefix}pointer-events-off`);
            if ( this._pot ) clearTimeout(this._pot);
          });

          this.element.addEventListener('mousemove', e=>{
            if ( this.element.classList.contains(`${this._prefix}can-drag`) ) {
              let directionX = this.mouseX - e.screenX;
              if ( Math.abs(directionX) > 1 ) {
                if ( ! this.element.classList.contains(`${this._prefix}css-slider--no-drag`) ) {
                  this.element.classList.add(`${this._prefix}pointer-events-off`);
                }
                if ( ( ! this._rtl && directionX > 0 ) || ( this._rtl && directionX < 0 ) ) {
                  this.changeSlide('next');
                  this.element.classList.remove(`${this._prefix}can-drag`);
                } else if ( ( ! this._rtl && directionX < 0 ) || ( this._rtl && directionX > 0 ) ) {
                  this.changeSlide('prev');
                  this.element.classList.remove(`${this._prefix}can-drag`);
                }
              }
            }
          });

        }

        // helper for browser that don't support smooth scrolling

        if ( ! ( "scrollBehavior" in document.documentElement.style ) && ! this._touchScreen ) {
          this.element.classList.add(`${this._prefix}force-disable-snapping`);
        }

        if ( ! this._touchScreen ) {
          this.element.ondragstart = e => {
            e.preventDefault();      
          }
        }

      }

      // dispatch ready event

      this.classList.add(`${this._prefix}enabled`);
      this.sliderEnabled = true;
      this.dispatchEvent(this._readyEvent);

      // check for autoplay

      if ( parseInt(this.o.autoplay) > 0 ) {
        this._initAutoplay();
      }

      // force first slide before the browser has a chance to paint another index
      if ( this.o.forceFirstSlide && this._forcedSlideChange === false ) {
        this._forcedSlideChange = true;
        this.changeSlide(0, 'auto');
      }

    }

    changeSlide(direction, behavior='smooth'){

      // function that changes the slide, either by word (next/prev) or index

      let loopWrap = null;

      if ( direction == 'next' ) {
        if ( this.index+1 < this.length ) {
          this.index++;
        } else if ( this.o.loop && this._loopNextClone ) {
          this.index = 0;
          loopWrap = 'next';
        }
      } else if ( direction == 'prev') {
        if ( this.index-1 >= 0 ) {
          this.index--;
        } else if ( this.o.loop && this._loopPrevClone ) {
          this.index = this.length - 1;
          loopWrap = 'prev';
        }
      } else if ( parseInt(direction) >= 0 ) {
        const requestedIndex = parseInt(direction);
        if ( requestedIndex >= 0 && requestedIndex < this.length ) {
          this.index = requestedIndex;
        }
      }

      this._sliderBlockScroll = true;
      if ( this._sliderBlockScrollTimer ) {
        clearTimeout(this._sliderBlockScrollTimer);
        this._sliderBlockScrollTimer = null;
      }

      // During an infinite-loop wrap, keep scroll/index observers locked
      // until the clone -> real-slide correction is completely finished.
      // This prevents two correction systems from fighting each other.
      if ( ! loopWrap ) {
        this._sliderBlockScrollTimer = setTimeout(()=>{
          this._sliderBlockScroll = false;
          this._sliderBlockScrollTimer = null;
        }, 500);
      }

      let targetItem = this.indexedItems[this.index];

      if ( loopWrap == 'next' ) {
        targetItem = this._loopNextClone;
      } else if ( loopWrap == 'prev' ) {
        targetItem = this._loopPrevClone;
      }

      const left = targetItem ? this._getScrollLeftForItem(targetItem) : 0;

      this.checkSlide();
      this.element.scrollTo({
        top: 0,
        left: left,
        behavior: behavior
      });
      this.dispatchEvent(this._changeEvent);

      if ( loopWrap ) {
        this._scheduleLoopCorrection(this.index, behavior, targetItem);
      }

    }

    checkSlide(){  
      
      // checks slide after index change and updates navigation / viewport

      if ( this.o.navigation ) {
        this.prevEl.classList.remove(`${this._prefix}disabled`);
        this.nextEl.classList.remove(`${this._prefix}disabled`);
        if ( ! this.o.loop ) {
          if ( this.index == 0 ) {
            this.prevEl.classList.add(`${this._prefix}disabled`);
          }
          if ( this.index == this.length - 1 ) {
            this.nextEl.classList.add(`${this._prefix}disabled`);
          }
        }
      }

      if ( this.o.thumbnails && this.thumbnails ) {
        this.thumbnails.forEach(elm=>{elm.classList.remove(`${this._prefix}active`)});
        this.thumbnails[this.index].classList.add(`${this._prefix}active`);
      }

      if ( this.o.indexNav ) {
        this.indexEl.textContent = this.index+1;
      }
      
      if ( this.o.autoHeight ) {
        this.viewport.style.height = this.indexedItems[this.index].offsetHeight + 'px';
      } 
      
      this.indexedItems.forEach((elm,i)=>{
        if ( i == this.index ) {
          elm.classList.add(`${this._prefix}css-slide-active`);
        } else {
          elm.classList.remove(`${this._prefix}css-slide-active`);
        }
      });

      if ( ! this.o.disableTabNavigation ) {
        const maxStart = Math.max(0, this.items.length - this.slidesPerPage); 
        const start = this.index * this.slidesPerPage;
        const adjustedStart = Math.min(start, maxStart);
        const end = adjustedStart + this.slidesPerPage;
        this.items.forEach((elm) => {
          elm.setAttribute('inert', '');
        });
        for ( let i = adjustedStart; i < end && i < this.items.length; i++ ) {
          const elm = this.items[i];
          elm.removeAttribute('inert');
        }
      } 

      if ( parseInt(this.o.autoplay) > 0 ) {
        this._initAutoplay();
      }

    }

    afterAppend(){
      if ( this.o.loop ) {
        this._removeLoopClones();
      }
      this.items = this.o.loop
        ? Array.from(this.querySelectorAll(`${this.o.selector}`)).filter(elm => !elm.hasAttribute('data-css-slider-loop-clone'))
        : this.querySelectorAll(`${this.o.selector}`);
      if ( this.o.loop && this.items.length > 1 ) {
        this._createLoopClones();
      }
    }

    _initAutoplay(){
      if ( this._autoplayInterval ) {
        clearInterval(this._autoplayInterval);
      }
      this._autoplayInterval = setInterval(()=>{
        if ( this.index + 1 == this.length ) {
          if ( this.o.loop ) {
            this.changeSlide('next');
          } else {
            this.changeSlide(0);
          }
        } else {
          this.changeSlide('next');
        }
      }, parseInt(this.o.autoplay));
      this.classList.remove(`${this._prefix}autoplay-running`);
      this.offsetWidth;
      this.classList.add(`${this._prefix}autoplay-running`);
    }

    _sanitizeLoopClone(clone, logicalIndex){
      clone.setAttribute('data-css-slider-loop-clone', 'true');
      clone.setAttribute('data-css-slider-loop-index', logicalIndex);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.classList.remove(`${this._prefix}css-slide-active`);

      if ( clone.id ) {
        clone.removeAttribute('id');
      }
      clone.querySelectorAll('[id]').forEach(elm=>{
        elm.removeAttribute('id');
      });
      clone.querySelectorAll('video').forEach(video=>{
        video.removeAttribute('autoplay');
        video.muted = true;
      });

      return clone;
    }

    _createLoopClones(){
      this._removeLoopClones();

      if ( ! this.items || this.items.length < 2 ) {
        return;
      }

      const track = this.items[0].parentElement;
      if ( ! track ) {
        return;
      }

      const lastIndex = this.items.length - 1;
      const prevClone = this._sanitizeLoopClone(this.items[lastIndex].cloneNode(true), lastIndex);
      track.insertBefore(prevClone, this.items[0]);

      const appendCount = Math.min(2, this.items.length);
      const nextClones = [];

      for ( let i = 0; i < appendCount; i++ ) {
        const clone = this._sanitizeLoopClone(this.items[i].cloneNode(true), i);
        track.appendChild(clone);
        nextClones.push(clone);
      }

      this._loopPrevClone = prevClone;
      this._loopNextClone = nextClones[0];
      this._loopClones = [prevClone, ...nextClones];
    }

    _removeLoopClones(){
      if ( this._loopClones ) {
        this._loopClones.forEach(clone=>{
          if ( clone && clone.parentNode ) {
            clone.parentNode.removeChild(clone);
          }
        });
      }
      this._loopClones = [];
      this._loopPrevClone = null;
      this._loopNextClone = null;
    }

    _destroyLoop(){
      if ( this._loopScrollEvent && this.element ) {
        this.element.removeEventListener(this._loopScrollEventType || 'scroll', this._loopScrollEvent);
      }
      this._loopScrollEvent = null;
      this._loopScrollEventType = null;

      if ( this._loopCorrectionTimer ) {
        clearTimeout(this._loopCorrectionTimer);
      }
      this._loopCorrectionTimer = null;

      if ( this._loopCorrectionFrame ) {
        cancelAnimationFrame(this._loopCorrectionFrame);
      }
      this._loopCorrectionFrame = null;

      if ( this._loopScrollEndHandler && this.element ) {
        this.element.removeEventListener('scrollend', this._loopScrollEndHandler);
      }
      this._loopScrollEndHandler = null;

      if ( this._sliderBlockScrollTimer ) {
        clearTimeout(this._sliderBlockScrollTimer);
      }
      this._sliderBlockScrollTimer = null;

      this._removeLoopClones();
    }

    _getScrollLeftForItem(item){
      if ( ! item ) {
        return 0;
      }

      const referenceItem = this.indexedItems[0] || this.items[0];
      const referenceStyle = referenceItem ? getComputedStyle(referenceItem) : null;
      const marginLeft = referenceStyle ? parseInt(referenceStyle.marginLeft) || 0 : 0;

      if ( this._rtl && this.slidesPerPage > 1 ) {
        const sliderWidth = this.o.sliderWidthSelector
          ? document.querySelector(this.o.sliderWidthSelector).offsetWidth
          : this.querySelector(`.${this._prefix}css-slider-container`).offsetWidth;
        return (sliderWidth - (item.offsetLeft - marginLeft)) * -1;
      }

      return item.offsetLeft - marginLeft;
    }

    _positionLoopAtCurrent(){
      if ( ! this.o.loop || ! this.indexedItems || ! this.indexedItems.length ) {
        return;
      }

      const target = this.indexedItems[Math.min(this.index, this.indexedItems.length - 1)];
      if ( ! target ) {
        return;
      }

      this._sliderBlockScroll = true;
      this.element.classList.add(`${this._prefix}disable-snapping`);
      this.element.scrollTo({
        top: 0,
        left: this._getScrollLeftForItem(target),
        behavior: 'auto'
      });

      requestAnimationFrame(()=>{
        this.element.classList.remove(`${this._prefix}disable-snapping`);
        this._sliderBlockScroll = false;
      });
    }

    _scheduleLoopCorrection(logicalIndex, behavior='smooth', visualTarget=null){
      if ( ! this.o.loop || ! this.indexedItems[logicalIndex] ) {
        this._sliderBlockScroll = false;
        return;
      }

      if ( this._loopCorrectionTimer ) {
        clearTimeout(this._loopCorrectionTimer);
        this._loopCorrectionTimer = null;
      }

      if ( this._loopCorrectionFrame ) {
        cancelAnimationFrame(this._loopCorrectionFrame);
        this._loopCorrectionFrame = null;
      }

      if ( this._loopScrollEndHandler ) {
        this.element.removeEventListener('scrollend', this._loopScrollEndHandler);
        this._loopScrollEndHandler = null;
      }

      let corrected = false;

      const correctPosition = ()=>{
        if ( corrected ) {
          return;
        }
        corrected = true;

        if ( this._loopScrollEndHandler ) {
          this.element.removeEventListener('scrollend', this._loopScrollEndHandler);
          this._loopScrollEndHandler = null;
        }

        if ( this._loopCorrectionTimer ) {
          clearTimeout(this._loopCorrectionTimer);
          this._loopCorrectionTimer = null;
        }

        if ( this._loopCorrectionFrame ) {
          cancelAnimationFrame(this._loopCorrectionFrame);
          this._loopCorrectionFrame = null;
        }

        const target = this.indexedItems[logicalIndex];

        this.index = logicalIndex;
        this.checkSlide();

        this.element.classList.add(`${this._prefix}disable-snapping`);
        this.element.scrollTo({
          top: 0,
          left: this._getScrollLeftForItem(target),
          behavior: 'auto'
        });

        // Keep the lock for two frames so the browser can commit the
        // clone -> real-slide jump before normal scroll detection resumes.
        requestAnimationFrame(()=>{
          requestAnimationFrame(()=>{
            this.element.classList.remove(`${this._prefix}disable-snapping`);
            this._sliderBlockScroll = false;
          });
        });
      };

      if ( behavior == 'auto' ) {
        requestAnimationFrame(correctPosition);
        return;
      }

      // Modern browsers: correct only after the smooth scroll has actually ended.
      if ( 'onscrollend' in this.element ) {
        this._loopScrollEndHandler = correctPosition;
        this.element.addEventListener('scrollend', this._loopScrollEndHandler, { once: true });

        // Safety fallback only. Long enough not to cut a smooth scroll short.
        this._loopCorrectionTimer = setTimeout(correctPosition, 1600);
        return;
      }

      // Fallback browsers: wait until the clone target is reached and the
      // scroll position has settled for consecutive frames.
      const expectedLeft = visualTarget
        ? this._getScrollLeftForItem(visualTarget)
        : this.element.scrollLeft;

      const startedAt = performance.now();
      let previousLeft = this.element.scrollLeft;
      let stableFrames = 0;

      const waitForSettle = ()=>{
        const currentLeft = this.element.scrollLeft;
        const nearTarget = Math.abs(currentLeft - expectedLeft) <= 1.5;
        const stable = Math.abs(currentLeft - previousLeft) <= 0.5;

        if ( nearTarget && stable ) {
          stableFrames++;
        } else {
          stableFrames = 0;
        }

        previousLeft = currentLeft;

        if ( stableFrames >= 2 || performance.now() - startedAt > 1600 ) {
          correctPosition();
          return;
        }

        this._loopCorrectionFrame = requestAnimationFrame(waitForSettle);
      };

      this._loopCorrectionFrame = requestAnimationFrame(waitForSettle);
    }

    _initLoopScrollWatcher(){
      if ( ! this.o.loop || ! this.element || this._loopScrollEvent ) {
        return;
      }

      const handleLoopBoundary = ()=>{
        if ( this._sliderBlockScroll || ! this._loopClones || ! this._loopClones.length ) {
          return;
        }

        let nearestClone = null;
        let nearestDistance = Infinity;

        this._loopClones.forEach(clone=>{
          const distance = Math.abs(this.element.scrollLeft - this._getScrollLeftForItem(clone));
          if ( distance < nearestDistance ) {
            nearestDistance = distance;
            nearestClone = clone;
          }
        });

        if ( ! nearestClone ) {
          return;
        }

        const tolerance = Math.max(12, nearestClone.getBoundingClientRect().width * 0.08);
        if ( nearestDistance > tolerance ) {
          return;
        }

        const logicalIndex = parseInt(nearestClone.getAttribute('data-css-slider-loop-index'));
        if ( Number.isNaN(logicalIndex) || ! this.indexedItems[logicalIndex] ) {
          return;
        }

        this.index = logicalIndex;
        this._sliderBlockScroll = true;
        this.checkSlide();

        this.element.classList.add(`${this._prefix}disable-snapping`);
        this.element.scrollTo({
          top: 0,
          left: this._getScrollLeftForItem(this.indexedItems[logicalIndex]),
          behavior: 'auto'
        });

        requestAnimationFrame(()=>{
          this.element.classList.remove(`${this._prefix}disable-snapping`);
          this._sliderBlockScroll = false;
          this.dispatchEvent(this._changeEvent);
        });
      };

      if ( 'onscrollend' in this.element ) {
        this._loopScrollEventType = 'scrollend';
        this._loopScrollEvent = handleLoopBoundary;
      } else {
        this._loopScrollEventType = 'scroll';
        this._loopScrollEvent = debounce(handleLoopBoundary, 120);
      }

      this.element.addEventListener(this._loopScrollEventType, this._loopScrollEvent, { passive: true });
    }

    resetSlider(nojump=false,resetIndex=true){

      let slidesWidth = 0,
          page = 0,
          pages = 0,
          totalWidth = this.o.sliderWidthSelector ? document.querySelector(this.o.sliderWidthSelector).offsetWidth : this.querySelector(`.${this._prefix}css-slider-container`).offsetWidth,// - 20,
          hideNavigation = false;

      // reset entire slider

      this.slidesPerPage = 0;
      this.indexedItems = [];
      if ( this._loopClones ) {
        this._loopClones.forEach(clone=>clone.classList.remove(`${this._prefix}css-slide--snap`));
      }
      this.element.classList.add(`${this._prefix}disable-snapping`);
      if ( this.OBSERVER ) {
        this.OBSERVER.disconnect();
      }

      // find out how many pages (slides there are now)

      this.items.forEach((elm, i)=>{
        elm.classList.remove(`${this._prefix}css-slide--snap`);
        slidesWidth += elm.getBoundingClientRect().width;
        if ( slidesWidth > totalWidth && this.slidesPerPage == 0 ) {
          this.slidesPerPage = i;
        }
      }); 

      if ( this.slidesPerPage == 0 ) {
        this.slidesPerPage = this.items.length;
        hideNavigation = true;
      } 

      if ( this.o.forceOneSlidePerPage ) {
        this.slidesPerPage = 1;
        if ( this.o.loop ) {
          hideNavigation = this.items.length <= 1;
        }
      }

      const sliderDifference = totalWidth - slidesWidth;
      if ( sliderDifference < 60 ) {
        this.setAttribute('data-slides-per-page-difference', 'small')
      } else if ( sliderDifference >= 60 ) {
        this.setAttribute('data-slides-per-page-difference', 'large')
      }

      // set each slide for observer
      
      this.items.forEach((elm, i) => {
        if ( i % this.slidesPerPage == 0  ) {
          elm.classList.add(`${this._prefix}css-slide--snap`);
          elm.setAttribute('data-index', page++);
          if ( this.OBSERVER ) {
            this.OBSERVER.observe(elm);
          }
        }
      });

      this.indexedItems = this.o.loop
        ? Array.from(this.items).filter(elm=>elm.classList.contains(`${this._prefix}css-slide--snap`))
        : this.querySelectorAll(`${this.o.selector}.${this._prefix}css-slide--snap`);

      if ( this.o.loop && this._loopClones ) {
        this._loopClones.forEach(clone=>clone.classList.add(`${this._prefix}css-slide--snap`));
      }

      if ( resetIndex ) {
        this.index = 0;
      }
      this.length = Math.ceil(this.items.length / this.slidesPerPage);

      // recreate navigation

      if ( this.o.thumbnails ) {
        this.thumbnailsEl.innerHTML = '';
        for ( let i = 0; i < this.length; i++ ) {
          let dot = document.createElement('span');
          dot.classList.add(`${this._prefix}css-slider-dot`);
          dot.dataset.index = i;
          this.thumbnailsEl.appendChild(dot);
          if ( ! this.o.thumbnailsNoInteraction ) {
            dot.addEventListener('click', (e)=>{
              this.changeSlide(e.target.dataset.index);
            });
            dot.setAttribute('aria-label', window.KROWN.settings.locales.slider_thumbnail_label.replace('{{ count }}', i+1));
            dot.setAttribute('role', 'button');
            dot.setAttribute('tabindex', 0);
            dot.addEventListener('keydown', e=>{
              if ( e.keyCode == window.KEYCODES.RETURN ) {
                dot.click();
              }
            });
          }
        }
        this.thumbnailsEl.style.setProperty('--size', this.length);
        this.thumbnails = this.thumbnailsEl.querySelectorAll(`.${this._prefix}css-slider-dot`);
      }

      if ( this.o.indexNav ) {
        this.indexEl.textContent = this.index+1;
        this.lengthEl.textContent = this.length;
      }

      // hide navigation if only one slide

      if ( hideNavigation ) {
        this.element.classList.add(`${this._prefix}css-slider--no-drag`);
        if ( this.o.navigation ) {
          this.prevEl.style.display = 'none';
          this.nextEl.style.display = 'none';
        }
        if ( this.o.thumbnails ) {
          this.thumbnailsEl.style.display = 'none';
        }
      } else {
        this.element.classList.remove(`${this._prefix}css-slider--no-drag`);
        if ( this.o.navigation ) {
          this.prevEl.style.display = 'block';
          this.nextEl.style.display = 'block';
        }
        if ( this.o.thumbnails ) {
          this.thumbnailsEl.style.display = 'block';
        }
      }

      this.checkSlide();

      if ( ! nojump ) {
        const resetTarget = this.o.loop && this.indexedItems.length ? this.indexedItems[this.index] : null;
        this.element.scrollTo({
          top: 0,
          left: resetTarget ? this._getScrollLeftForItem(resetTarget) : 0,
          behavior: 'auto'
        })
      }
      this.element.classList.remove(`${this._prefix}disable-snapping`);
      this.setAttribute('data-slider-length', this.length);

      this.dispatchEvent(this._resetEvent);

    }

  }

  if ( typeof customElements.get('css-slider') == 'undefined' ) {
    customElements.define('css-slider', CSSSlider);
  }

  document.addEventListener('shopify:section:load', (e)=>{
    if ( e.target.classList.contains(`mount-css-slider`) && e.target.querySelector('css-slider') ) {
      e.target.querySelectorAll('css-slider').forEach(slider=>{if(slider.sliderEnabled)slider.resetSlider()});
    }
  });
  
}